import { includes } from 'lodash-es';

import {
  Job,
  JobScreeningQuestionSelectedQuestions,
} from '../../../common/interfaces/entities';
import {
  CustomQuestionPlainTexts,
  customQuestionPlainTextToFieldName,
  JobFormState,
  JobPostingPayload,
  SelectedQuestionsFormState,
} from '../common/constants';

const isScreeningQuestionSelected = (
  screeningQuestions: JobFormState['screeningQuestions'],
  type: SelectedQuestionsFormState
) => screeningQuestions?.selectedQuestions?.includes(type);

const convertSelectedQuestionsFormStateToPayLoad = (
  selectedQuestions: SelectedQuestionsFormState[] | undefined,
  isVIPActive: boolean
): JobScreeningQuestionSelectedQuestions[] | undefined =>
  selectedQuestions?.flatMap(selectedQuestion => {
    if (includes(CustomQuestionPlainTexts, selectedQuestion)) {
      return isVIPActive
        ? [JobScreeningQuestionSelectedQuestions.CUSTOM_PLAIN_TEXT]
        : [];
    }

    return [selectedQuestion as JobScreeningQuestionSelectedQuestions];
  });

const convertCustomPlainTextScreeningQuestionsToPayload = (
  screeningQuestions: JobFormState['screeningQuestions']
) => {
  const customQuestionPlainTexts =
    screeningQuestions?.selectedQuestions?.filter(
      (selectedQuestion): selectedQuestion is CustomQuestionPlainTexts =>
        includes(CustomQuestionPlainTexts, selectedQuestion)
    );
  if (!customQuestionPlainTexts?.length) {
    return null;
  }

  return {
    customQuestionPlainTexts: customQuestionPlainTexts.map(
      question =>
        screeningQuestions?.[customQuestionPlainTextToFieldName[question]] || ''
    ),
  };
};

export const convertScreeningQuestionFormStateToPayload = ({
  screeningQuestions,
  isEdit,
  isVIPActive,
}: {
  screeningQuestions: JobFormState['screeningQuestions'];
  isEdit: boolean;
  isVIPActive: boolean;
}): Pick<JobPostingPayload, 'screeningQuestions'> | null => {
  const selectedQuestions = convertSelectedQuestionsFormStateToPayLoad(
    screeningQuestions?.selectedQuestions,
    isVIPActive
  );
  const includeScreeningQuestion =
    !isEdit && selectedQuestions && selectedQuestions.length > 0;

  if (!includeScreeningQuestion) {
    return null;
  }

  return {
    screeningQuestions: {
      selectedQuestions,
      ...(isScreeningQuestionSelected(
        screeningQuestions,
        SelectedQuestionsFormState.INTRO_VIDEO
      ) && {
        introVideoPreferredLanguage:
          screeningQuestions?.introVideoPreferredLanguage,
      }),
      ...(isScreeningQuestionSelected(
        screeningQuestions,
        SelectedQuestionsFormState.INDUSTRY_EXPERIENCE
      ) && {
        industryIds: screeningQuestions?.industries?.map(
          industry => industry.id
        ),
      }),
      ...(isScreeningQuestionSelected(
        screeningQuestions,
        SelectedQuestionsFormState.LANGUAGE_PROFICIENCY
      ) && {
        languageCodes: screeningQuestions?.languages?.map(
          language => language.code
        ),
      }),
      ...(isScreeningQuestionSelected(
        screeningQuestions,
        SelectedQuestionsFormState.DOCUMENT_CERTIFICATE_AVAILABILITY
      ) && {
        documentAndCertificateIds:
          screeningQuestions?.documentAndCertificates?.map(
            document => document.id
          ),
      }),
      ...(isVIPActive
        ? convertCustomPlainTextScreeningQuestionsToPayload(screeningQuestions)
        : {}),
    },
  };
};

const convertSelectedQuestionsResponseToFormState = (
  selectedQuestions?: JobScreeningQuestionSelectedQuestions[]
): SelectedQuestionsFormState[] | undefined => {
  if (!selectedQuestions) return undefined;

  let customPlainTextCounter = 0;

  return selectedQuestions.map(selectedQuestion => {
    if (
      selectedQuestion ===
      JobScreeningQuestionSelectedQuestions.CUSTOM_PLAIN_TEXT
    ) {
      return Object.values(CustomQuestionPlainTexts)[customPlainTextCounter++];
    }
    return selectedQuestion;
  });
};

export const convertScreeningQuestionResponseToFormState = ({
  screeningQuestions,
  isVIPActive,
}: {
  screeningQuestions: Job['screeningQuestions'];
  isVIPActive: boolean;
}): JobFormState['screeningQuestions'] => {
  const noScreeningQuestion =
    !screeningQuestions || screeningQuestions?.selectedQuestions.length < 1;

  if (noScreeningQuestion) {
    return undefined;
  }

  const customPlainTexts = isVIPActive
    ? screeningQuestions.customQuestionPlainTexts
    : undefined;

  const selectedQuestionsToConvert = isVIPActive
    ? screeningQuestions.selectedQuestions
    : screeningQuestions.selectedQuestions.filter(
        q => q !== JobScreeningQuestionSelectedQuestions.CUSTOM_PLAIN_TEXT
      );

  return {
    selectedQuestions: convertSelectedQuestionsResponseToFormState(
      selectedQuestionsToConvert
    ),
    introVideoPreferredLanguage:
      screeningQuestions.introVideoPreferredLanguageDetail?.code || undefined,
    industries:
      screeningQuestions.industries.length > 0
        ? screeningQuestions.industries
        : undefined,
    documentAndCertificates:
      screeningQuestions.documentAndCertificates.length > 0
        ? screeningQuestions.documentAndCertificates
        : undefined,
    customQuestionPlainTextA: customPlainTexts?.[0] || '',
    customQuestionPlainTextB: customPlainTexts?.[1] || '',
    customQuestionPlainTextC: customPlainTexts?.[2] || '',
    languages:
      screeningQuestions.languages.length > 0
        ? screeningQuestions.languages
        : undefined,
  };
};
